/** @fileoverview Generic layout tokens shared by Grid rendering and the screen component stack. */

import type { CSSProperties } from 'react';

import type { FabrikComponent, FabrikFlexItemSize } from '../../types/apps';

export const GRID_GAPS = { sm: '0.5rem', md: '1rem', lg: '1.5rem' } as const;
/*
 * Starting widths of a flex grid item; an item wraps to a new row when its basis no longer fits.
 * With no basis an item that grows starts from 0, so grow values split the row exactly; one that
 * does not grow starts from its own content width.
 */
const FLEX_BASES = { auto: 'auto', sm: '12rem', md: '18rem', lg: '24rem', xl: '32rem' } as const;

/* Grid: one column below md. Flex: a wrapping row whose items share the free space by grow and start from their basis. */
const GRID_CLASSES =
  'grid min-w-0 grid-cols-[repeat(var(--fabrik-grid-columns),minmax(0,1fr))] gap-(--fabrik-grid-gap) max-md:grid-cols-[minmax(0,1fr)]';
const GRID_ITEM_CLASSES =
  'min-w-0 col-span-(--fabrik-grid-span) [align-self:var(--fabrik-grid-align)] max-md:col-span-1';
const FLEX_CLASSES = 'flex min-w-0 flex-wrap gap-(--fabrik-grid-gap)';
const FLEX_ITEM_CLASSES =
  'min-w-0 flex-[var(--fabrik-flex-grow)_1_var(--fabrik-flex-basis)] [align-self:var(--fabrik-grid-align)]';

type GridItemLook = FabrikFlexItemSize & {
  span?: number;
  align?: 'start' | 'center' | 'end' | 'stretch';
};

/**
 * Gives the class and CSS variables of a grid's container: columns, or a wrapping flex row.
 * @param grid the grid or form layout
 */
export const gridContainerProps = ({
  mode,
  columns,
  gap,
}: {
  mode?: 'columns' | 'flex';
  columns?: number;
  gap: keyof typeof GRID_GAPS;
}) => ({
  className: mode === 'flex' ? FLEX_CLASSES : GRID_CLASSES,
  style: { '--fabrik-grid-columns': columns, '--fabrik-grid-gap': GRID_GAPS[gap] } as CSSProperties,
});

/**
 * Gives the class and CSS variables of one grid item: its column span, or its flex sizing.
 * @param mode the mode of the grid the item sits in
 * @param item the item's span, grow, basis and alignment; `fullRow` makes a flex item take a whole row
 */
export const gridItemProps = (
  mode: 'columns' | 'flex' | undefined,
  { span, grow, basis, align, fullRow }: GridItemLook & { fullRow?: boolean }
) =>
  mode === 'flex'
    ? {
        className: FLEX_ITEM_CLASSES,
        style: {
          '--fabrik-flex-grow': fullRow ? 1 : grow ?? 1,
          '--fabrik-flex-basis': fullRow
            ? '100%'
            : basis
            ? FLEX_BASES[basis]
            : (grow ?? 1) > 0
            ? '0%'
            : 'auto',
          '--fabrik-grid-align': align ?? 'stretch',
        } as CSSProperties,
      }
    : {
        className: GRID_ITEM_CLASSES,
        style: {
          '--fabrik-grid-span': span ?? 1,
          '--fabrik-grid-align': align ?? 'stretch',
        } as CSSProperties,
      };

type TextVariant = Extract<FabrikComponent, { kind: 'text' }>['style']['variant'];

const textVariantOf = (component: FabrikComponent): TextVariant | undefined =>
  component.kind === 'text' ? component.style.variant : undefined;

const TITLE_VARIANTS = new Set<TextVariant | undefined>(['display', 'heading']);
const LEAD_VARIANTS = new Set<TextVariant | undefined>(['display', 'heading', 'subheading']);

/**
 * Picks the space above a screen component from what sits before it, so related blocks group
 * together: tight inside a title group, wider between blocks, widest before a new heading.
 * @param previous the component rendered just above, if any
 * @param current the component to place
 * @param isSidePanel true in the narrow side panel, where the widest gaps shrink
 */
export const spacingBefore = (
  previous: FabrikComponent | undefined,
  current: FabrikComponent,
  isSidePanel?: boolean
): string => {
  if (!previous) return '';
  const previousVariant = textVariantOf(previous);
  const currentVariant = textVariantOf(current);
  if (previousVariant === 'eyebrow') return 'mt-1';
  if (TITLE_VARIANTS.has(previousVariant) && currentVariant === 'subheading') return 'mt-app-tight';
  if (TITLE_VARIANTS.has(currentVariant) || currentVariant === 'subheading')
    return isSidePanel || previousVariant ? 'mt-app-block' : 'mt-app-section';
  if (LEAD_VARIANTS.has(previousVariant)) return currentVariant ? 'mt-app-tight' : 'mt-app-group';
  if (previousVariant === 'body' && currentVariant === 'note') return 'mt-1';
  if (previousVariant) return 'mt-app-group';
  return 'mt-app-block';
};
